<template>
  <div class="max-w-[900px] mx-auto my-8 px-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-8">
      <div class="flex items-center gap-3">
        <Link
          :href="adminTechniquesPath"
          class="text-[#6c7a77] hover:text-teal-600 transition-colors"
        >
          <ArrowLeft :size="18" />
        </Link>
        <h1 class="font-[Poppins] font-bold text-2xl text-[#191c1e]">
          {{ isNew ? "Ny teknik" : "Redigera teknik" }}
        </h1>
      </div>
      <div class="flex items-center gap-3">
        <a
          v-if="techniquePath"
          :href="techniquePath"
          target="_blank"
          class="text-sm text-teal-600 hover:underline"
        >
          Visa sida ↗
        </a>
        <Button type="submit" form="technique-form" :disabled="form.processing">
          {{ isNew ? "Skapa" : "Spara" }}
        </Button>
      </div>
    </div>

    <!-- Errors -->
    <div
      v-if="errors?.length"
      class="mb-6 bg-red-50 border border-red-200 rounded-xl p-4"
    >
      <p v-for="err in errors" :key="err" class="text-sm text-red-600">
        {{ err }}
      </p>
    </div>

    <form id="technique-form" @submit.prevent="submit">
      <!-- Static settings section -->
      <div class="rounded-2xl border border-[#e6e8ea] p-6 mb-6 space-y-5">
        <h2 class="font-[Poppins] font-semibold text-base text-[#191c1e] mb-1">
          Grundinformation
        </h2>

        <!-- Published toggle -->
        <div class="flex items-center gap-3">
          <input
            id="published"
            v-model="form.published"
            type="checkbox"
            class="w-4 h-4 accent-teal-500 cursor-pointer"
          />
          <label
            for="published"
            class="text-sm font-medium text-[#191c1e] cursor-pointer"
          >
            Publicerad (synlig på /tools)
          </label>
        </div>

        <!-- Title -->
        <div>
          <Label for="title" class="mb-1.5 block"
            >Titel <span class="text-red-500">*</span></Label
          >
          <Input
            id="title"
            v-model="form.title"
            placeholder="t.ex. Hur du bryner smör"
          />
        </div>

        <!-- Slug -->
        <div>
          <Label for="slug" class="mb-1.5 block">Slug (URL)</Label>
          <Input
            id="slug"
            v-model="form.slug"
            placeholder="hur-du-bryner-smor"
          />
          <p class="text-xs text-[#6c7a77] mt-1">
            Lämna tomt för att generera automatiskt från titeln.
          </p>
        </div>

        <!-- Badge + color -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <Label for="badge" class="mb-1.5 block"
              >Badge-text <span class="text-red-500">*</span></Label
            >
            <Input id="badge" v-model="form.badge" placeholder="t.ex. BASICS" />
          </div>
          <div>
            <Label for="badge_color" class="mb-1.5 block"
              >Badge-färg <span class="text-red-500">*</span></Label
            >
            <div class="flex items-center gap-2">
              <input
                id="badge_color"
                v-model="form.badge_color"
                type="color"
                class="w-10 h-10 rounded cursor-pointer border border-[#e6e8ea]"
              />
              <Input
                v-model="form.badge_color"
                placeholder="#f59e0b"
                class="flex-1"
              />
            </div>
          </div>
        </div>

        <!-- Read time -->
        <div>
          <Label for="read_time" class="mb-1.5 block">Lästid</Label>
          <Input
            id="read_time"
            v-model="form.read_time"
            placeholder="t.ex. 5 min läsning"
          />
        </div>

        <!-- Intro -->
        <div>
          <Label for="intro" class="mb-1.5 block">Intro / Ingress</Label>
          <textarea
            id="intro"
            v-model="form.intro"
            rows="3"
            placeholder="Kort sammanfattning som visas på listkortet och överst i artikeln."
            class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
          />
        </div>

        <!-- Hero image -->
        <div>
          <Label class="mb-1.5 block">Hero-bild</Label>
          <div
            v-if="form.heroImageUrl && !newHeroImage"
            class="mb-3 flex items-start gap-3"
          >
            <img
              :src="form.heroImageUrl"
              alt="Hero"
              class="h-28 w-auto rounded-lg object-cover"
            />
            <button
              type="button"
              class="text-xs text-red-500 hover:underline mt-1 cursor-pointer"
              @click="
                newHeroImage = null;
                form.heroImageUrl = null;
              "
            >
              Ta bort
            </button>
          </div>
          <input
            type="file"
            accept="image/*"
            class="text-sm"
            @change="(e) => (newHeroImage = e.target.files[0] ?? null)"
          />
        </div>

        <!-- Takeaways / Kom ihåg -->
        <div>
          <Label class="mb-1.5 block"
            >Kom ihåg (punktlista i sidopanelen)</Label
          >
          <p class="text-xs text-[#6c7a77] mb-2">Skriv en punkt per rad.</p>
          <textarea
            v-model="takeawaysText"
            rows="4"
            placeholder="Använd alltid ljus panna&#10;Rör om kontinuerligt&#10;Guldbrunt = klart"
            class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
          />
        </div>

        <!-- Related techniques -->
        <div>
          <Label class="mb-1.5 block">Relaterade tekniker</Label>
          <p class="text-xs text-[#6c7a77] mb-2">
            Välj tekniker som visas i "Fler tekniker"-sektionen.
          </p>
          <div
            class="space-y-1.5 max-h-48 overflow-y-auto border border-[#e6e8ea] rounded-lg p-3"
          >
            <label
              v-for="t in allTechniques.filter((t) => t.id !== technique.id)"
              :key="t.id"
              class="flex items-center gap-2 cursor-pointer text-sm text-[#191c1e] hover:text-teal-600"
            >
              <input
                type="checkbox"
                :value="t.id"
                v-model="form.related_technique_ids"
                class="accent-teal-500"
              />
              {{ t.title }}
            </label>
            <p v-if="allTechniques.length <= 1" class="text-xs text-[#6c7a77]">
              Inga andra tekniker att länka ännu.
            </p>
          </div>
        </div>

        <!-- Linked tools -->
        <div>
          <Label class="mb-1.5 block">Länkade verktyg</Label>
          <p class="text-xs text-[#6c7a77] mb-2">
            Välj verktyg som visas i "Relaterade tekniker och
            verktyg"-sektionen.
          </p>
          <div class="space-y-1.5 border border-[#e6e8ea] rounded-lg p-3">
            <label
              v-for="tool in availableTools"
              :key="tool.key"
              class="flex items-center gap-2 cursor-pointer text-sm text-[#191c1e] hover:text-teal-600"
            >
              <input
                type="checkbox"
                :value="tool.key"
                v-model="form.linked_tool_keys"
                class="accent-teal-500"
              />
              {{ tool.label }}
            </label>
            <p v-if="!availableTools?.length" class="text-xs text-[#6c7a77]">
              Inga verktyg tillgängliga ännu.
            </p>
          </div>
        </div>
      </div>

      <!-- Block editor section -->
      <div class="rounded-2xl border border-[#e6e8ea] p-6 mb-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="font-[Poppins] font-semibold text-base text-[#191c1e]">
            Artikelinnehåll
          </h2>
          <p class="text-xs text-[#6c7a77]">Dra för att ändra ordning</p>
        </div>

        <!-- Draggable block list -->
        <Draggable
          v-model="blocks"
          item-key="uid"
          handle=".drag-handle"
          ghost-class="opacity-30"
          class="space-y-3 mb-4"
        >
          <template #item="{ element: block, index }">
            <div
              class="border border-[#e6e8ea] rounded-xl bg-white overflow-hidden"
            >
              <!-- Block header -->
              <div
                class="flex items-center gap-2 px-4 py-2 bg-[#f9fafb] border-b border-[#e6e8ea]"
              >
                <span
                  class="drag-handle cursor-grab active:cursor-grabbing text-[#9ca3af] hover:text-[#6c7a77]"
                >
                  <GripVertical :size="16" />
                </span>
                <span
                  class="text-[11px] font-bold uppercase tracking-widest text-[#6c7a77]"
                >
                  {{ blockLabel(block.type) }}
                </span>
                <button
                  type="button"
                  class="ml-auto text-red-400 hover:text-red-600 cursor-pointer"
                  @click="removeBlock(index)"
                >
                  <X :size="14" />
                </button>
              </div>

              <!-- Block body -->
              <div class="p-4 space-y-3">
                <!-- text block -->
                <template v-if="block.type === 'text'">
                  <textarea
                    v-model="block.content"
                    rows="4"
                    placeholder="Skriv ett stycke..."
                    class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
                  />
                </template>

                <!-- heading block -->
                <template v-else-if="block.type === 'heading'">
                  <Input v-model="block.content" placeholder="Rubrik..." />
                </template>

                <!-- tip block -->
                <template v-else-if="block.type === 'tip'">
                  <Input
                    v-model="block.title"
                    placeholder="Tipsrubrik (t.ex. Emmas tips: välj rätt panna)"
                    class="mb-2"
                  />
                  <textarea
                    v-model="block.content"
                    rows="3"
                    placeholder="Tipstext..."
                    class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
                  />
                </template>

                <!-- warning block -->
                <template v-else-if="block.type === 'warning'">
                  <Input
                    v-model="block.title"
                    placeholder="Varningsrubrik (t.ex. Var försiktig)"
                    class="mb-2"
                  />
                  <textarea
                    v-model="block.content"
                    rows="3"
                    placeholder="Varningstext..."
                    class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
                  />
                </template>

                <!-- pullquote block -->
                <template v-else-if="block.type === 'pullquote'">
                  <textarea
                    v-model="block.content"
                    rows="2"
                    placeholder='"Det doftar som en nybakad kaka fast det bara är smör."'
                    class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
                  />
                </template>

                <!-- keypoints block -->
                <template v-else-if="block.type === 'keypoints'">
                  <Input
                    v-model="block.label"
                    placeholder="Label (t.ex. Kom ihåg)"
                    class="mb-2"
                  />
                  <p class="text-xs text-[#6c7a77] mb-1.5">
                    Punkter (en per rad):
                  </p>
                  <textarea
                    :value="(block.items || []).join('\n')"
                    rows="4"
                    placeholder="Punkt 1&#10;Punkt 2&#10;Punkt 3"
                    class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
                    @input="(e) => (block.items = e.target.value.split('\n'))"
                  />
                </template>

                <!-- image block -->
                <template v-else-if="block.type === 'image'">
                  <div v-if="block.src" class="mb-2">
                    <img
                      :src="block.src"
                      alt="block image"
                      class="h-24 w-auto rounded-lg object-cover"
                    />
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    class="text-sm mb-2"
                    @change="(e) => handleBlockImage(e, block)"
                  />
                  <Input
                    v-model="block.caption"
                    placeholder="Bildtext / Caption"
                  />
                </template>
              </div>
            </div>
          </template>
        </Draggable>

        <!-- Add block buttons -->
        <div class="flex flex-wrap gap-2">
          <button
            v-for="type in blockTypes"
            :key="type.value"
            type="button"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e6e8ea] text-sm text-[#3c4947] hover:bg-[#f2f4f6] hover:border-teal-400 transition-colors cursor-pointer"
            @click="addBlock(type.value)"
          >
            <component :is="type.icon" :size="14" class="text-teal-500" />
            {{ type.label }}
          </button>
        </div>
      </div>

      <!-- Submit row (sticky bottom) -->
      <div class="flex justify-end gap-3 pb-8">
        <Link
          :href="adminTechniquesPath"
          class="inline-flex items-center justify-center rounded-md border border-[#e6e8ea] px-4 py-2 text-sm font-medium text-[#3c4947] hover:bg-[#f2f4f6] transition-colors"
        >
          Avbryt
        </Link>
        <Button type="submit" :disabled="form.processing">
          {{ isNew ? "Skapa teknik" : "Spara ändringar" }}
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, shallowRef, computed } from "vue";
import { Link, router } from "@inertiajs/vue3";
import Draggable from "vuedraggable";
import {
  ArrowLeft,
  GripVertical,
  X,
  AlignLeft,
  Heading,
  Lightbulb,
  AlertTriangle,
  Quote,
  List,
  Image,
} from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const props = defineProps({
  technique: { type: Object, required: true },
  allTechniques: { type: Array, default: () => [] },
  actionPath: { type: String, required: true },
  adminTechniquesPath: { type: String, required: true },
  techniquePath: { type: String, default: null },
  availableTools: { type: Array, default: () => [] },
  errors: { type: Array, default: () => [] },
});

const isNew = computed(() => !props.technique.id);

// Form state
const form = ref({
  title: props.technique.title ?? "",
  slug: props.technique.slug ?? "",
  badge: props.technique.badge ?? "",
  badge_color: props.technique.badgeColor ?? "#14b8a6",
  read_time: props.technique.readTime ?? "",
  intro: props.technique.intro ?? "",
  published: props.technique.published ?? false,
  heroImageUrl: props.technique.heroImageUrl ?? null,
  related_technique_ids: props.technique.relatedTechniqueIds ?? [],
  linked_tool_keys: props.technique.linkedToolKeys ?? [],
});

// Takeaways as line-separated text
const takeawaysText = ref((props.technique.takeaways ?? []).join("\n"));

// Body blocks — each gets a uid for keying
let uidCounter = 0;
const blocks = ref(
  (props.technique.bodyBlocks ?? []).map((b) => ({ ...b, uid: ++uidCounter })),
);

const newHeroImage = shallowRef(null);

// Plain object (not ref) so File objects are never wrapped in a Vue Proxy,
// which would break native FormData.append file detection.
const blockImageFiles = {};

const blockTypes = [
  { value: "text", label: "Text", icon: AlignLeft },
  { value: "heading", label: "Rubrik", icon: Heading },
  { value: "tip", label: "Tips", icon: Lightbulb },
  { value: "warning", label: "Varning", icon: AlertTriangle },
  { value: "pullquote", label: "Citat", icon: Quote },
  { value: "keypoints", label: "Nyckelpoints", icon: List },
  { value: "image", label: "Bild", icon: Image },
];

function blockLabel(type) {
  return blockTypes.find((b) => b.value === type)?.label ?? type;
}

function addBlock(type) {
  const defaults = {
    text: { type: "text", content: "" },
    heading: { type: "heading", content: "" },
    tip: { type: "tip", title: "", content: "" },
    warning: { type: "warning", title: "", content: "" },
    pullquote: { type: "pullquote", content: "" },
    keypoints: { type: "keypoints", label: "", items: [] },
    image: { type: "image", src: null, caption: "" },
  };
  blocks.value.push({ ...defaults[type], uid: ++uidCounter });
}

function removeBlock(index) {
  blocks.value.splice(index, 1);
}

function handleBlockImage(event, block) {
  const file = event.target.files[0];
  if (!file) return;
  blockImageFiles[block.uid] = file;
  block.src = URL.createObjectURL(file);
}

function submit() {
  // Serialize takeaways from line-separated text
  const takeawaysArray = takeawaysText.value
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

  // Serialize blocks — strip uids, but keep uid on image blocks with a pending
  // file upload so the backend can correlate the uploaded file with the block.
  const cleanBlocks = blocks.value.map((b) => {
    const { uid, ...block } = b;
    if (b.type === "image" && blockImageFiles[b.uid]) {
      // Clear the temporary blob URL; the real URL will be written by the backend.
      return { ...block, uid, src: null };
    }
    return block;
  });

  const data = new FormData();
  data.append("technique[title]", form.value.title);
  data.append("technique[slug]", form.value.slug);
  data.append("technique[badge]", form.value.badge);
  data.append("technique[badge_color]", form.value.badge_color);
  data.append("technique[read_time]", form.value.read_time);
  data.append("technique[intro]", form.value.intro);
  data.append("technique[published]", form.value.published ? "1" : "0");
  data.append("technique[takeaways]", JSON.stringify(takeawaysArray));
  data.append("technique[body_blocks]", JSON.stringify(cleanBlocks));

  // Block images — sent as technique[block_images][uid] = File
  Object.entries(blockImageFiles).forEach(([uid, file]) => {
    data.append(`technique[block_images][${uid}]`, file);
  });

  // Hero image
  if (newHeroImage.value) {
    data.append("technique[hero_image]", newHeroImage.value);
  }

  // Related techniques
  form.value.related_technique_ids.forEach((id) => {
    data.append("technique[related_technique_ids][]", id);
  });

  // Linked tools
  form.value.linked_tool_keys.forEach((key) => {
    data.append("technique[linked_tool_keys][]", key);
  });

  const method = isNew.value ? "post" : "patch";
  router[method](props.actionPath, data, {
    forceFormData: true,
    preserveScroll: true,
  });
}
</script>
